<template>
  <v-container fluid>

    <PageTitle icon="fal fa-tachometer-alt-fast">
      My Dashboard
    </PageTitle>

    <v-form @submit.prevent=""
            :disabled="loading"
            class="mb-8">
      <v-card>
        <v-card-text class="pb-0">
          <v-row>
            <v-col cols="3">
              <v-select v-model="form.range"
                        label="Time Period"
                        outlined
                        placeholder=" "
                        :items="dateRanges"
                        item-value="id"
                        item-text="name"
                        hide-details
                        @change="updateDateRange">
              </v-select>
            </v-col>
            <v-col cols="12"
                   md="3">
              <v-menu ref="startMenu"
                      v-model="startMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                      z-index="999">
                <template v-slot:activator="{on, attrs}">
                  <v-text-field v-model="form.start"
                                label="From"
                                outlined
                                readonly
                                hide-details
                                v-bind="attrs"
                                v-on="on">
                  </v-text-field>
                </template>

                <v-date-picker v-model="form.start"
                               :min="minStartDate"
                               :max="maxStartDate"
                               @input="form.range = 'custom'; startMenu = false; endMenu = true;">
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12"
                   md="3">

              <v-menu ref="endMenu"
                      v-model="endMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                      z-index="999">
                <template v-slot:activator="{on, attrs}">
                  <v-text-field v-model="form.end"
                                label="To"
                                outlined
                                readonly
                                hide-details
                                v-bind="attrs"
                                v-on="on">
                  </v-text-field>
                </template>

                <v-date-picker v-model="form.end"
                               :min="minEndDate"
                               :max="maxEndDate"
                               @input="form.range = 'custom'; startMenu = false; endMenu = false; fetchData()">
                </v-date-picker>
              </v-menu>

            </v-col>
            <v-col cols="12"
                   md="3"
                   class="d-flex align-center">
              <v-btn type="submit"
                     color="primary"
                     :disabled="loading"
                     @click.prevent="fetchData">
                Apply
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-progress-linear indeterminate
                           v-if="loading"
                           color="primary">
        </v-progress-linear>
      </v-card>
    </v-form>

    <template v-if="loading">
      <v-row>
        <v-col cols="12">
          <v-skeleton-loader type="card"
                             height="350">
          </v-skeleton-loader>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col v-for="n in 2"
               cols="12"
               md="6"
               :key="n">
          <v-skeleton-loader type="card">
          </v-skeleton-loader>
        </v-col>
      </v-row>

      <v-skeleton-loader type="table"
                         class="mb-4">
      </v-skeleton-loader>

      <v-skeleton-loader type="table"
                         class="mb-4">
      </v-skeleton-loader>
    </template>
    <template v-else>
      <UserEmailDailyMetricsChart :start="form.start"
                                  :end="form.end"
                                  :sender="user"
                                  class="mb-4">
        <template v-slot:title>
          <v-icon left>
            fal fa-fw fa-paper-plane
          </v-icon>
          My Email Activity
        </template>
      </UserEmailDailyMetricsChart>

      <v-card v-if="emailMetrics"
              class="mb-4">
        <v-card-title>
          <v-icon left>
            fal fa-fw fa-calculator
          </v-icon>
          My Email Metrics
        </v-card-title>
        <v-row class="mb-4">
          <v-col cols="12"
                 md="3">
            <MetricCard title="Delivered"
                        :count="emailMetrics.delivered_count ?? 0"
                        :rate="emailMetrics.delivered_rate ?? 0"
                        flat
                        tile>
            </MetricCard>
          </v-col>

          <v-col cols="12"
                 md="3">
            <MetricCard title="Opened"
                        :count="emailMetrics.opened_count ?? 0"
                        :rate="emailMetrics.opened_rate ?? 0"
                        flat
                        tile>
            </MetricCard>
          </v-col>

          <v-col cols="12"
                 md="3">
            <MetricCard title="Clicked"
                        :count="emailMetrics.clicked_count ?? 0"
                        :rate="emailMetrics.clicked_rate ?? 0"
                        flat
                        tile>
            </MetricCard>
          </v-col>

          <v-col cols="12"
                 md="3">
            <MetricCard title="Failed"
                        :count="emailMetrics.failed_count ?? 0"
                        :rate="emailMetrics.failed_rate ?? 0"
                        flat
                        tile>
            </MetricCard>
          </v-col>
        </v-row>
      </v-card>

      <v-card v-if="team.campaigns_enabled && campaigns.length"
              class="mb-4">
        <v-card-title>
          <v-icon left>
            fal fa-fw fa-bullhorn
          </v-icon>
          My recent campaigns
          <v-spacer></v-spacer>
          <v-text-field v-model="campaignSearch"
                        append-icon="fal fa-search"
                        small
                        label="Search"
                        single-line
                        hide-details
                        class="pt-0">
          </v-text-field>
          <v-btn icon
                 small
                 class="ml-4"
                 :disabled="campaigns.length === 0"
                 @click.prevent="exportCampaigns">
            <v-icon>
              fal fa-fw fa-download
            </v-icon>
          </v-btn>
          <help-dialog v-if="1===2"
                       icon>
          </help-dialog>
        </v-card-title>
        <v-data-table :headers="campaignHeaders"
                      :items="campaigns"
                      :items-per-page="5"
                      :loading="campaignsBusy"
                      :search="campaignSearch"
                      sort-by="created_at"
                      sort-desc>
          <template v-slot:item.name="{item: campaign}">
            <router-link :to="{name: `${$root.routePrefix}.mailcoach.campaign.show`, params: {campaignID: campaign.id}}">
              {{ campaign.name }}
            </router-link>
          </template>
          <template v-slot:item.created_at="{item: campaign}">
            <v-tooltip left>
              <template v-slot:activator="{on, attrs}">
                <div v-bind="attrs"
                     v-on="on"
                     class="text-nowrap">
                  {{ campaign.created_at | datetime('MMM D, YYYY') }}
                </div>
              </template>
              <div>
                {{ campaign.created_at | datetime('MMM D, YYYY h:mm a') }}
              </div>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>

      <v-card class="mb-4">
        <v-card-title>
          <v-icon left>
            fal fa-fw fa-envelope-open-text
          </v-icon>
          Who did I email recently?
          <v-spacer></v-spacer>
          <v-text-field v-model="messageSearch"
                        prepend-inner-icon="fal fa-search"
                        label="Search"
                        outlined
                        dense
                        hide-details
                        class="pt-0">
          </v-text-field>
          <v-btn icon
                 small
                 class="ml-4"
                 :disabled="messages.length === 0"
                 @click.prevent="exportMessages">
            <v-icon>
              fal fa-fw fa-download
            </v-icon>
          </v-btn>
          <help-dialog v-if="1===2"
                       icon>
          </help-dialog>
        </v-card-title>
        <v-data-table :headers="messageHeaders"
                      :items="messages"
                      :items-per-page="5"
                      :loading="messagesBusy"
                      :search="messageSearch"
                      sort-by="created_at"
                      sort-desc>
          <template v-slot:item.status="{item: message}">
            <message-status :message="message">
            </message-status>
          </template>
          <template v-slot:item.created_at="{item: message}">
            <div class="text-nowrap">
              <message-link :message="message"
                            date-format="MMM D, YYYY"
                            :use-dialog="true">
              </message-link>
            </div>
          </template>
          <template v-slot:item.delivered_at="{item: message}">
            <v-tooltip left
                       v-if="message.delivered_at">
              <template v-slot:activator="{on, attrs}">
                <div v-bind="attrs"
                     v-on="on"
                     class="text-nowrap">
                  {{ message.delivered_at | datetime('MMM D, YYYY') }}
                </div>
              </template>
              <div>
                {{ message.delivered_at | datetime('MMM D, YYYY h:mm a') }}
              </div>
            </v-tooltip>
          </template>
          <template v-slot:item.opened_at="{item: message}">
            <v-tooltip left
                       v-if="message.opened_at">
              <template v-slot:activator="{on, attrs}">
                <div v-bind="attrs"
                     v-on="on"
                     class="text-nowrap">
                  {{ message.opened_at | datetime('MMM D, YYYY') }}
                </div>
              </template>
              <div>
                {{ message.opened_at | datetime('MMM D, YYYY h:mm a') }}
              </div>
            </v-tooltip>
          </template>
          <template v-slot:item.clicked_at="{item: message}">
            <v-tooltip left
                       v-if="message.clicked_at">
              <template v-slot:activator="{on, attrs}">
                <div v-bind="attrs"
                     v-on="on"
                     class="text-nowrap">
                  {{ message.clicked_at | datetime('MMM D, YYYY') }}
                </div>
              </template>
              <div>
                {{ message.clicked_at | datetime('MMM D, YYYY h:mm a') }}
              </div>
            </v-tooltip>
          </template>
          <template v-slot:item.failed_at="{item: message}">
            <v-tooltip left
                       v-if="message.failed_at">
              <template v-slot:activator="{on, attrs}">
                <div v-bind="attrs"
                     v-on="on"
                     class="text-nowrap">
                  {{ message.failed_at | datetime('MMM D, YYYY') }}
                </div>
              </template>
              <div>
                {{ message.failed_at | datetime('MMM D, YYYY h:mm a') }}
              </div>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>

      <v-card v-if="templates.length"
              class="mb-4">
        <v-card-title>
          <v-icon left>
            fal fa-fw fa-envelope
          </v-icon>
          Which templates have I sent?
          <v-spacer></v-spacer>
          <v-text-field v-model="templatesSearch"
                        prepend-inner-icon="fal fa-search"
                        label="Search"
                        outlined
                        dense
                        hide-details
                        class="pt-0">
          </v-text-field>

          <v-btn v-if="1===2"
                 icon
                 small
                 class="ml-4">
            <v-icon>
              fal fa-fw fa-download
            </v-icon>
          </v-btn>
          <help-dialog v-if="1===2"
                       icon>
          </help-dialog>
        </v-card-title>
        <v-data-table :headers="templateHeaders"
                      :items="templates"
                      :items-per-page="5"
                      :loading="templatesBusy"
                      :search="templatesSearch"
                      sort-by="user_metrics.created_count"
                      sort-desc>
          <template v-slot:item.name="{item: template}">
            <router-link :to="{name: `${$root.routePrefix}.share_template.dashboard`, params: {templateID: template.id}}">
              {{ template.name }}
            </router-link>
          </template>
        </v-data-table>
      </v-card>

      <v-card v-if="sessions.length"
              class="mb-4">
        <v-card-title>
          <v-icon left>
            fal fa-fw fa-book-reader
          </v-icon>
          Who is looking at my content?
          <v-spacer></v-spacer>
          <v-text-field v-model="sessionsSearch"
                        prepend-inner-icon="fal fa-search"
                        small
                        label="Search"
                        outlined
                        dense
                        hide-details
                        class="pt-0">
          </v-text-field>
          <v-btn v-if="1===2"
                 icon
                 small
                 class="ml-4"
                 :disabled="sessions.length === 0"
                 @click.prevent="exportViewerSessions">
            <v-icon>
              fal fa-fw fa-download
            </v-icon>
          </v-btn>
          <help-dialog v-if="1===2"
                       icon>
          </help-dialog>
        </v-card-title>
        <v-data-table :headers="sessionHeaders"
                      :items="sessions"
                      :items-per-page="5"
                      :loading="sessionsBusy"
                      :search="sessionsSearch"
                      sort-by="started_at"
                      sort-desc>
          <template v-slot:item.userContact.name="{item: session}">
            <template v-if="session.userContact.name">
              {{ session.userContact.name }}
            </template>
          </template>
          <template v-slot:item.userContact.email="{item: session}">
            {{ session.userContact.email }}
          </template>
          <template v-slot:item.started_at="{item: session}">
            <router-link :to="{name: `${$root.routePrefix}.session.show`, params: {sessionID: session.id}}"
                         class="text-nowrap">
              {{ session.started_at | datetime('MMM D, YYYY') }}
            </router-link>
          </template>
          <template v-slot:item.duration="{item: session}">
            {{ session.duration_for_humans }}
          </template>
          <template v-slot:item.percent_viewed="{item: session}">
            {{ session.percent_viewed }}%
          </template>
        </v-data-table>
      </v-card>

      <v-card v-if="deviceSessions.length">
        <v-card-title>
          <v-icon left>
            fal fa-fw fa-presentation
          </v-icon>
          What did I present recently?
          <v-spacer></v-spacer>
          <v-text-field v-model="deviceSessionSearch"
                        prepend-inner-icon="fal fa-search"
                        label="Search"
                        outlined
                        dense
                        hide-details
                        class="pt-0">
          </v-text-field>
          <v-btn icon
                 small
                 class="ml-4"
                 :disabled="deviceSessions.length === 0"
                 @click.prevent="exportDeviceSessions">
            <v-icon>
              fal fa-fw fa-download
            </v-icon>
          </v-btn>
          <help-dialog v-if="1===2"
                       icon>
          </help-dialog>
        </v-card-title>
        <v-data-table :headers="deviceSessionHeaders"
                      :items="deviceSessions"
                      :items-per-page="5"
                      :loading="deviceSessionsBusy"
                      :search="deviceSessionSearch"
                      sort-by="started_at"
                      sort-desc>
          <template v-slot:item.started_at="{item: session}">
            <v-tooltip left>
              <template v-slot:activator="{on, attrs}">
                <div v-bind="attrs"
                     v-on="on"
                     class="text-nowrap">
                  {{ session.started_at | datetime('MMM D, YYYY') }}
                </div>
              </template>
              <div>
                {{ session.started_at | datetime('MMM D, YYYY h:mm a') }}
              </div>
            </v-tooltip>
          </template>
          <template v-slot:item.duration="{item: session}">
            {{ session.duration_for_humans }}
          </template>
          <template v-slot:item.live_mode="{item: session}">
            {{ session.live_mode ? 'Yes' : 'No' }}
          </template>
          <template v-slot:item.closed_properly="{item: session}">
            {{ session.closed_properly ? 'Yes' : 'No' }}
          </template>
        </v-data-table>
      </v-card>
    </template>

  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {orderBy} from 'lodash';
import mailgunService from '../../../../ml/services/mailgunService';
import MessagesTable from '../mailgun/components/MessagesTable';
import {Form} from 'vform';
import find from 'lodash/find';
import moment from 'moment-timezone';
import MessageStatus from '../mailgun/components/MessageStatus';
import mailcoachService from '../../services/mailcoachService';
import analyticsService from '../../../../ml/services/analyticsService';
import deviceEventService from '../../../../ml/services/deviceEventService';
import HelpDialog from '../../components/HelpDialog';
import MessageLink from '../mailgun/components/MessageLink';
import shareTemplateService from "../../../../ml/services/shareTemplateService";
import axios from '../../services/axiosService';
import PageTitle from '../../components/PageTitle.vue';
import MetricCard from '../../components/MetricCard.vue';
import UserEmailDailyMetricsChart from '../mailgun/components/UserEmailDailyMetricsChart.vue';

export default {
  components: {UserEmailDailyMetricsChart, MetricCard, PageTitle, MessageLink, HelpDialog, MessageStatus, MessagesTable},
  data() {
    return {
      loading: false,
      groupMetrics: [],
      presentationMetrics: [],
      regionMetrics: [],
      teamMetrics: [],
      userMetrics: [],
      teamHeatMetrics: [],
      typeMetrics: [],
      form: new Form({
        group_id: '',
        team_id: '',
        user_id: '',
        range: 'past_30',
        start: '',
        end: '',
        start_at: '',
        end_at: '',
        days: 0,
      }),
      startDialog: false,
      endDialog: false,
      startMenu: false,
      endMenu: false,
      dateRanges: [
        {id: 'past_7', name: 'Past 7 days', start: moment().add(-7, 'day').startOf('day'), end: moment().endOf('day')},
        {id: 'past_15', name: 'Past 15 days', start: moment().add(-15, 'day').startOf('day'), end: moment().endOf('day')},
        {isDefault: true, id: 'past_30', name: 'Past 30 days', start: moment().add(-30, 'day').startOf('day'), end: moment().endOf('day')},
        {id: 'past_45', name: 'Past 45 days', start: moment().add(-45, 'day').startOf('day'), end: moment().endOf('day')},
        {id: 'past_60', name: 'Past 60 days', start: moment().add(-60, 'day').startOf('day'), end: moment().endOf('day')},
        {id: 'past_90', name: 'Past 90 days', start: moment().add(-90, 'day').startOf('day'), end: moment().endOf('day')},
        {id: 'this_week', name: 'This week', start: moment().startOf('week'), end: moment().endOf('day')},
        {id: 'this_month', name: 'This month', start: moment().startOf('month'), end: moment().endOf('day')},
        {id: 'this_quarter', name: 'This quarter', start: moment().startOf('quarter'), end: moment().endOf('day')},
        {id: 'this_year', name: 'This year', start: moment().startOf('year'), end: moment().endOf('day')},
        {id: 'last_week', name: 'Last week', start: moment().add(-1, 'week').startOf('week'), end: moment().add(-1, 'week').endOf('week')},
        {id: 'last_month', name: 'Last month', start: moment().add(-1, 'month').startOf('month'), end: moment().add(-1, 'month').endOf('month')},
        {id: 'last_quarter', name: 'Last quarter', start: moment().add(-1, 'quarter').startOf('quarter'), end: moment().add(-1, 'quarter').endOf('quarter')},
        {id: 'last_year', name: 'Last year', start: moment().add(-1, 'year').startOf('year'), end: moment().add(-1, 'year').endOf('year')},
        {isCustom: true, id: 'custom', name: 'Custom'},
      ],

      campaignsBusy: false,
      campaigns: [],
      campaignHeaders: [
        {
          text: 'Sent',
          value: 'created_at',
        },
        {
          text: 'Name',
          value: 'name',
        },
        {
          text: 'List',
          value: 'list.name',
        },
        {
          text: 'Delivered',
          value: 'metrics.delivered_count',
        },
        {
          text: 'Opened',
          value: 'metrics.unique_open_count',
        },
        {
          text: 'Clicked',
          value: 'metrics.unique_click_count',
        },
      ],
      campaignSearch: null,

      deviceSessionsBusy: false,
      deviceSessions: [],
      deviceSessionHeaders: [
        {
          text: 'Date',
          value: 'started_at',
        },
        {
          text: 'Content',
          value: 'presentation.name',
          width: '300px',
        },
        {
          text: 'Name',
          value: 'contact.name',
        },
        {
          text: 'Duration',
          value: 'duration',
        },
        {
          text: 'Platform',
          value: 'platform',
        },
        {
          text: 'Live Mode',
          value: 'live_mode',
        },
        {
          text: 'Closed Properly',
          value: 'closed_properly',
        },
      ],
      deviceSessionSearch: null,

      messagesBusy: false,
      messages: [],
      messageHeaders: [
        {
          text: 'Sent',
          value: 'created_at',
        },
        {
          text: 'Recipient',
          value: 'recipient',
        },
        {
          text: 'Subject',
          value: 'subject',
        },
        {
          text: 'Status',
          value: 'status',
        },
        {
          text: 'Delivered',
          value: 'delivered_at',
        },
        {
          text: 'Opened',
          value: 'opened_at',
        },
        {
          text: 'Clicked',
          value: 'clicked_at',
        },
        {
          text: 'Failed',
          value: 'failed_at',
        },
      ],
      messagesMeta: null,
      messageSearch: null,

      sessions: [],
      sessionsBusy: false,
      sessionHeaders: [
        {
          text: 'Date',
          value: 'started_at',
        },
        {
          text: 'Duration',
          value: 'duration',
        },
        {
          text: 'Percent Viewed',
          value: 'percent_viewed',
          align: 'center',
        },
        {
          text: 'Content',
          value: 'presentation.name',
          width: '300px',
        },
        {
          text: 'Name',
          value: 'userContact.name',
        },
        {
          text: 'Email',
          value: 'userContact.email',
        },
      ],
      sessionsSearch: null,

      templates: [],
      templatesBusy: false,
      templateHeaders: [
        {
          text: 'Template Name',
          value: 'name',
          cellClass: 'text-nowrap',
        },
        /*{
          text: 'Subject',
          value: 'subject',
        },*/
        {
          text: 'Sent',
          value: 'user_metrics.created_count',
        },
        {
          text: 'Delivered',
          value: 'user_metrics.delivered_count',
        },
        {
          text: 'Opened',
          value: 'user_metrics.unique_open_count',
        },
        {
          text: 'Clicked',
          value: 'user_metrics.unique_click_count',
        },
        {
          text: 'Failed',
          value: 'user_metrics.unique_failed_count',
        },
      ],
      templatesSearch: null,
      emailMetrics: null,
    }
  },
  computed: {
    ...mapGetters([
      'isAdmin',
      'isMember',
      'team',
      'user',
    ]),
    defaultDateRange() {
      return find(this.dateRanges, r => r.isDefault);
    },
    minStartDate() {
      return moment(this.team.created_at).format('YYYY-MM-DD');
    },
    maxStartDate() {
      return moment().format('YYYY-MM-DD')
    },
    minEndDate() {
      //return moment(this.form.startDate).format('YYYY-MM-DD')
      return moment(this.team.created_at).add(1, 'day').format('YYYY-MM-DD');
    },
    maxEndDate() {
      return moment().format('YYYY-MM-DD')
    },
    groupCharts() {
      return !!this.form.group_id;
    },
    userCharts() {
      return !!this.form.user_id;
    },
    teamCharts() {
      return !this.groupCharts && !this.userCharts;
    },
  },
  created() {
    this.initData();
    this.initForm();
    this.fetchData();
  },
  methods: {
    initData() {
      //this.$store.dispatch('fetchGroups');
      //this.$store.dispatch('fetchUsers');
    },
    initForm() {
      this.initDates();
      this.form.team_id = this.team.id;
      this.form.user_id = this.user.id;
    },
    initDates() {
      if (this.defaultDateRange) {
        this.form.range = this.defaultDateRange.id;
        this.form.start = this.defaultDateRange.start.format('YYYY-MM-DD');
        this.form.end = this.defaultDateRange.end.format('YYYY-MM-DD');
        this.form.start_at = this.defaultDateRange.start.format('YYYY-MM-DD 00:00:00');
        this.form.end_at = this.defaultDateRange.end.format('YYYY-MM-DD 23:59:59');
        this.form.days = Math.abs(this.defaultDateRange.end.diff(this.defaultDateRange.start, 'days')); // MLD-6081
      }
    },
    resetForm() {
      this.initDates();
      this.form.group_id = '';
      this.form.user_id = '';
      this.fetchData();
    },
    selectUser() {
      this.form.group_id = '';
      this.fetchData();
    },
    async fetchData() {
      if (this.loading) {
        return;
      }
      try {
        this.loading = true;
        this.getCampaigns();
        this.getDeviceSessions();
        this.getMessages();
        this.getSessions();
        this.getTemplates(); // MLD-2823
        this.getEmailMetrics().then(); // MLD-6081

        const form = {
          start: this.form.start,
          end: this.form.end,
          team_id: this.form.team_id,
        }

        await axios.get(`/api/metrics/users/${this.form.user_id}`, {params: form})
          .then(response => {
            this.userMetrics = response.data
          });

      } catch (err) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    updateDateRange() {
      const dateRange = find(this.dateRanges, r => r.id === this.form.range);
      if (dateRange) {
        if (dateRange.isCustom) {
          this.startMenu = true;
          this.endMenu = false;
        } else {
          this.form.start = dateRange.start.format('YYYY-MM-DD');
          this.form.end = dateRange.end.format('YYYY-MM-DD');
          this.form.start_at = dateRange.start.format('YYYY-MM-DD 00:00:00');
          this.form.end_at = dateRange.end.format('YYYY-MM-DD 23:59:59');
          this.form.days = Math.abs(dateRange.end.diff(dateRange.start, 'days')); // MLD-6081
          this.fetchData();
        }
      }
    },
    getCampaigns() {
      if (!this.team.campaigns_enabled) {
        return;
      }
      if (this.campaignsBusy) {
        return;
      }
      this.campaignsBusy = true;
      const form = {
        start_at: this.form.start_at,
        end_at: this.form.end_at,
        user_id: this.user.id,
      }
      mailcoachService.getCampaigns(form)
        .then(data => {
          this.campaigns = orderBy(data, ['id'], ['desc']);
        })
        .catch(() => {
        })
        .finally(() => {
          this.campaignsBusy = false;
        })
    },
    getDeviceSessions() {
      if (this.deviceSessionsBusy) {
        return;
      }
      this.deviceSessionsBusy = true;
      const form = {
        started_at: this.form.start,
        ended_at: this.form.end,
        duration: 0,
        order_by: 'started_at',
        order_dir: 'desc',
        page: 1,
        per_page: 100,
        session_type: 'normal',
        user_id: this.user.id,
        team_id: this.user.current_team_id,
      }
      deviceEventService.searchSessions(form)
        .then(data => {
          this.deviceSessions = data.data;
        })
        .catch(() => {
        })
        .finally(() => {
          this.deviceSessionsBusy = false;
        })
    },
    getTemplates() { // MLD-2823
      if (this.templatesBusy) {
        return;
      }
      this.templatesBusy = true;
      const form = {}
      shareTemplateService.getTemplatesForUser(form)
        .then(data => {
          const templates = data.filter(template => template.user_metrics.created_count > 0);
          this.templates = orderBy(templates, ['user_metrics.created_count', 'slug'], ['desc', 'asc']);
        })
        .catch(() => {
        })
        .finally(() => {
          this.templatesBusy = false;
        })
    },
    getMessages() {
      if (this.messagesBusy) {
        return;
      }
      this.messagesBusy = true;
      const form = {
        start_at: this.form.start_at,
        end_at: this.form.end_at,
        order_by: 'id',
        order_dir: 'desc',
        per_page: 500,
        sender_id: this.user.id,
        team_id: this.user.current_team_id,
        tags: [
          'api_triggered_email',
          'campaign_email',
          'event_triggered_email',
          'shared_presentation',
          'share_template',
        ],
      }
      mailgunService.getMessages(form)
        .then(data => {
          this.messages = data.data;
          this.messagesMeta = data.meta;
        })
        .catch(() => {
        })
        .finally(() => {
          this.messagesBusy = false;
        })
    },
    getSessions() {
      if (this.sessionsBusy) {
        return;
      }
      this.sessionsBusy = true;
      const form = {
        limit: 100,
        offset: 0,
        order_by: 'created_at',
        order_dir: 'desc',
        user_id: this.user.id, // MLD-4192
      }
      analyticsService.getSessions(form)
        .then(data => {
          this.sessions = orderBy(data, ['started_at', 'created_at'], ['desc', 'desc']);
        })
        .catch(() => {
        })
        .finally(() => {
          this.sessionsBusy = false;
        })
    },
    exportMessages() { // MLD-4588
      const form = mailgunService.initMessagesForm();
      form.export = 1;
      form.sender_id = this.user.id;
      form.start_at = this.form.start_at;
      form.end_at = this.form.end_at;
      form.order_by = 'id';
      form.order_dir = 'asc';
      form.tags = [
        'api_triggered_email',
        'campaign_email',
        'event_triggered_email',
        'shared_presentation',
        'share_template'
      ];
      mailgunService.getMessages(form)
        .then(data => {
          let messages = [];
          if (data && data.data) {
            messages = data.data;
          } else {
            messages = data;
          }
          let filename = 'recent_emails.csv';
          // https://stackoverflow.com/questions/43432892/force-download-get-request-using-axios
          const blob = new Blob([messages], {type: 'text/csv'});
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.dispatchEvent(new MouseEvent(`click`, {bubbles: true, cancelable: true, view: window}));
        })
        .catch(err => {
          this.$toast.error('An error occurred.');
        });
    },
    exportCampaigns() { // MLD-4588
      const form = {
        start_at: this.form.start_at,
        end_at: this.form.end_at,
      }
      mailcoachService.exportCampaigns(form)
        .catch(() => {
        });
    },
    exportDeviceSessions() { // MLD-4588
      const form = {
        started_at: this.form.start,
        ended_at: this.form.end,
        duration: 0,
        order_by: 'started_at',
        order_dir: 'desc',
        session_type: 'normal',
        user_id: this.user.id,
        team_id: this.user.current_team_id,
      }
      axios.post(`/api/device-sessions/export`, form)
        .then(response => {
          let data = response.data;
          // https://stackoverflow.com/questions/43432892/force-download-get-request-using-axios
          let blob = new Blob([data], {type: 'text/csv'});
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `device_sessions.csv`;
          link.dispatchEvent(new MouseEvent(`click`, {bubbles: true, cancelable: true, view: window}));
        })
        .catch(() => {
        })
    },
    exportViewerSessions() { // MLD-4588
      const form = {}
      axios.post(`/api/sessions/export`, form)
        .then(response => {
          let data = response.data;
          // https://stackoverflow.com/questions/43432892/force-download-get-request-using-axios
          let blob = new Blob([data], {type: 'text/csv'});
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `viewer_sessions.csv`;
          link.dispatchEvent(new MouseEvent(`click`, {bubbles: true, cancelable: true, view: window}));
          return data;
        })
        .catch(() => {

        })
        .finally(() => {
          this.busy = false;
        })
    },
    async getEmailMetrics() { // MLD-6081
      const form = {
        start: this.form.start,
        end: this.form.end,
      }
      try {
        this.emailMetrics = await mailgunService.getEmailMetricsForCurrentUser(form)
      } catch (e) {
      }
    }
  }
}
</script>
